// Email delivery status interface
export interface EmailDeliveryStatus {
  id: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  error?: string;
  sentAt?: Date;
  deliveredAt?: Date;
  retryCount?: number;
  messageId?: string;
}


/**
 * Send user credentials email using our Express email API
 */
export const sendUserCredentialsEmail = async (userDetails: {
  name: string;
  email: string;
  password: string;
  role: string;
  company?: string;
  assignedSites?: Array<{
    id: string;
    name: string;
    address: string;
    photo?: string;
  }>;
}): Promise<string> => {
  console.log('Sending credentials email to:', userDetails.email);

  const apiEndpoint =
    window.location.hostname === 'localhost'
      ? 'http://localhost:3001/api/send-credentials'
      : new URL('/api/send-credentials', window.location.origin).href;

  const response = await fetch(apiEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      name: userDetails.name,
      email: userDetails.email,
      password: userDetails.password,
      role: userDetails.role,
      assignedSites: userDetails.assignedSites || [],
    }),
  });

  const text = await response.text();
  let result: { success?: boolean; messageId?: string; error?: string } = {};
  if (text?.trim()) {
    try {
      result = JSON.parse(text) as typeof result;
    } catch {
      if (!response.ok) {
        throw new Error(
          response.status === 404
            ? 'Email API not found (404). Deploy from project root with: npx wrangler pages deploy (includes Pages Functions for /api/send-credentials).'
            : `Email service error (${response.status}): ${text.slice(0, 200)}`
        );
      }
      throw new Error(
        'Email service returned non-JSON. Check that Cloudflare Pages serves /api/send-credentials (Functions) and not only static files.'
      );
    }
  }

  if (!response.ok) {
    throw new Error(
      result.error ||
        (response.status === 404
          ? 'Email API not found (404). Ensure functions/api/send-credentials is deployed with Pages.'
          : `Email request failed (${response.status}): ${text?.slice(0, 200) || response.statusText}`)
    );
  }

  if (result.success) {
    console.log('Credentials email sent successfully:', result.messageId);
    return result.messageId ?? '';
  }

  throw new Error(
    result.error ||
      'Email not sent. In Cloudflare Pages → Environment variables set RESEND_API_KEY (secret) and redeploy. Verify domain micclo.com in Resend so noreply@micclo.com is allowed.'
  );
};

/**
 * Monitor email delivery status (placeholder for Firebase extension compatibility)
 */
export const monitorEmailDelivery = (
  emailId: string,
  onStatusChange: (status: EmailDeliveryStatus) => void
): (() => void) => {
  // Placeholder implementation since we're not using Firebase extension anymore
  // In a real implementation, this could poll the email server API for status
  const mockStatus: EmailDeliveryStatus = {
    id: emailId,
    status: 'success',
    sentAt: new Date(),
    messageId: emailId
  };
  
  // Call callback immediately with mock success status
  setTimeout(() => onStatusChange(mockStatus), 100);
  
  // Return cleanup function
  return () => {};
};

/**
 * Test email delivery (for development)
 */
export const sendTestEmail = async (testEmail: string): Promise<string> => {
  return await sendUserCredentialsEmail({
    name: 'Test User',
    email: testEmail,
    password: 'TestPassword123!',
    role: 'Clerk',
    company: 'Test Company',
  });
};
